import {
  AutoComplete,
  ButtonGroup,
  Container,
  Form,
  FormItem,
  Loading,
  Modal,
} from '../../../../components';

import { useAnalytics } from './hooks/useAnalytics';
import { useAuth } from '../../../../hooks';
import {
  AnalyticsCard,
  PieChartCard,
  BarChartCard,
  BarChartModal,
  type PieChartData,
} from '.';
import {
  type AnalyticsPeriod,
  AnalyticsPeriosOptions,
  AnalyticsMetric,
} from '../../const';
import { processAnalyticsData } from './utils/processAnalyticsData';
import { Divider } from '@mui/material';
import { MetricsWithTags } from '../../../conversations/const';

export const DashboardTab = () => {
  const { organization_id: orgId } = useAuth();
  const {
    form,
    onChange,
    agentsOptions,
    data,
    formId,
    isSubmitting,
    isStatusModalOpen,
    setIsStatusModalOpen,
    isChartModalOpen,
    chartModalData,
    openChartModal,
    closeChartModal,
  } = useAnalytics();

  const { metrics, pieCharts, charts } = processAnalyticsData(data);

  const handleChartClick = (item: any) => {
    if (item.seriesData && item.seriesData.length > 0) {
      openChartModal(
        item.title,
        item.seriesData,
        item.unit,
        item.scale,
        item.type
      );
    }
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center m-4 gap-y-sm">
        <div className="flex gap-sm">
          <Form
            key={formId}
            initialValue={{ agentId: form?.agentId || '' }}
            onChange={values =>
              onChange(form ? { ...form, agentId: values.agentId } : null)
            }
          >
            <FormItem
              inline={true}
              name="agentId"
              label="Agent: "
              labelContainerClassName="flex flex-wrap"
              labelClassName="text-small text-secondary"
            >
              <AutoComplete options={agentsOptions || []} />
            </FormItem>
          </Form>
        </div>

        <div className="flex gap-x-sm items-center">
          <FormItem name="period" label="" inline={false}>
            <ButtonGroup
              buttons={AnalyticsPeriosOptions.map(option => ({
                action: option.value,
                label: option.label,
                active: form?.period === option.value,
              }))}
              orientation="horizontal"
              buttonClassName="p-4"
              labelClassName="whitespace-nowrap overflow-hidden text-ellipsis text-small font-semibold text-secondary"
              onClick={action =>
                onChange(
                  form ? { ...form, period: action as AnalyticsPeriod } : null
                )
              }
            />
          </FormItem>
        </div>
      </div>

      <Divider className="m-4" />

      {!form?.agentId ? (
        <Container>
          <div className="flex items-center justify-center min-h-[20vh]">
            <div className="text-center text-xl font-semibold text-secondary">
              Please select an agent
            </div>
          </div>
        </Container>
      ) : isSubmitting ? (
        <Container>
          <div className="flex items-center justify-center min-h-[20vh]">
            <Loading />
          </div>
        </Container>
      ) : (
        <>
          <div className="mx-2 sm:mx-4 lg:mx-sm">
            <div className="flex flex-col items-center pt-4 sm:pt-6 lg:pt-8 pb-4 sm:pb-6 lg:pb-lg">
              <div className="w-full max-w-screen-2xl px-2 sm:px-4 lg:px-4">
                <div className="block lg:hidden space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {metrics.slice(0, 3).map(metric => (
                      <div key={metric.id} className="w-full h-[120px]">
                        <AnalyticsCard
                          item={metric}
                          onChartClick={handleChartClick}
                        />
                      </div>
                    ))}
                  </div>

                  {pieCharts.find(
                    chart => chart.type === AnalyticsMetric.TOPIC_TAGS
                  ) && (
                    <div className="w-full h-[240px] sm:h-[280px]">
                      <PieChartCard
                        item={
                          pieCharts.find(
                            chart => chart.type === AnalyticsMetric.TOPIC_TAGS
                          )!
                        }
                        alternativeMetric={pieCharts.find(
                          chart => chart.type === AnalyticsMetric.MOOD_TAGS
                        )}
                        height="h-full"
                        agentId={form?.agentId}
                        period={form?.period}
                        orgId={orgId}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {pieCharts
                      .filter(chart => !MetricsWithTags.includes(chart.type))
                      .slice(0, 3)
                      .map(chart => (
                        <div
                          key={chart.id}
                          className="w-full h-[240px] sm:h-[280px]"
                        >
                          <PieChartCard
                            item={chart}
                            onClick={item => {
                              if (
                                item.type ===
                                AnalyticsMetric.SYSTEM_AVAILABILITY
                              ) {
                                setIsStatusModalOpen(true);
                              }
                            }}
                            agentId={form?.agentId}
                            period={form?.period}
                            orgId={orgId}
                          />
                        </div>
                      ))}

                    {pieCharts.find(
                      chart =>
                        chart.type === AnalyticsMetric.SYSTEM_AVAILABILITY
                    ) && (
                      <div className="w-full h-[240px] sm:h-[280px]">
                        <PieChartCard
                          item={
                            pieCharts.find(
                              chart =>
                                chart.type ===
                                AnalyticsMetric.SYSTEM_AVAILABILITY
                            )!
                          }
                          onClick={(item: PieChartData) => {
                            if (
                              item.type === AnalyticsMetric.SYSTEM_AVAILABILITY
                            ) {
                              setIsStatusModalOpen(true);
                            } else {
                              handleChartClick(item);
                            }
                          }}
                          agentId={form?.agentId}
                          period={form?.period}
                          orgId={orgId}
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {charts.map(chart => (
                      <div
                        key={chart.id}
                        className="w-full h-[240px] sm:h-[280px]"
                      >
                        <BarChartCard
                          item={chart}
                          period={form?.period}
                          height="h-full"
                          onChartClick={handleChartClick}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hidden lg:grid lg:grid-cols-5 gap-4 lg:gap-6">
                  <div className="col-span-4 grid grid-cols-3 gap-4 lg:gap-6 h-[120px]">
                    {metrics.slice(0, 3).map(metric => (
                      <div key={metric.id} className="w-full h-full">
                        <AnalyticsCard
                          item={metric}
                          onChartClick={handleChartClick}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="col-span-1 row-span-2 h-full">
                    {pieCharts.find(
                      chart => chart.type === AnalyticsMetric.TOPIC_TAGS
                    ) && (
                      <PieChartCard
                        item={
                          pieCharts.find(
                            chart => chart.type === AnalyticsMetric.TOPIC_TAGS
                          )!
                        }
                        alternativeMetric={pieCharts.find(
                          chart => chart.type === AnalyticsMetric.MOOD_TAGS
                        )}
                        height="h-full"
                        agentId={form?.agentId}
                        period={form?.period}
                        orgId={orgId}
                      />
                    )}
                  </div>

                  <div className="col-span-4 grid grid-cols-4 gap-4 lg:gap-6 h-[240px]">
                    {pieCharts
                      .filter(chart => !MetricsWithTags.includes(chart.type))
                      .slice(0, 4)
                      .map(chart => (
                        <div key={chart.id} className="w-full">
                          <PieChartCard
                            item={chart}
                            onClick={item => {
                              if (
                                item.type ===
                                AnalyticsMetric.SYSTEM_AVAILABILITY
                              ) {
                                setIsStatusModalOpen(true);
                              }
                            }}
                            onChartClick={handleChartClick}
                            agentId={form?.agentId}
                            period={form?.period}
                            orgId={orgId}
                          />
                        </div>
                      ))}
                  </div>

                  <div className="col-span-5 grid grid-cols-2 gap-4 lg:gap-6 h-[240px]">
                    {charts.map(chart => (
                      <div key={chart.id} className="w-full h-full">
                        <BarChartCard
                          item={chart}
                          period={form?.period}
                          height="h-full"
                          onChartClick={handleChartClick}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Modal
            size="big"
            title="System Status"
            open={isStatusModalOpen}
            onClose={() => setIsStatusModalOpen(false)}
            contentClassName="p-0"
          >
            <iframe
              src="https://status.nextlevel.ai/"
              className="w-full h-[80vh] border-0"
              title="System Status"
            />
          </Modal>

          {chartModalData && (
            <BarChartModal
              isOpen={isChartModalOpen}
              onClose={closeChartModal}
              title={chartModalData.title}
              data={chartModalData.data}
              period={form?.period}
              unit={chartModalData.unit}
              scale={chartModalData.scale}
              chartType={chartModalData.chartType}
            />
          )}
        </>
      )}
    </>
  );
};
